/**
 * Plausibility limits for a displayed measurement.
 *
 * These are **physiological possibility** bounds, not accuracy bands and not clinical
 * normal ranges. A value outside them is treated as a failed measurement rather than a
 * low-confidence one, so it is withheld.
 *
 * The spirometry bounds were widened deliberately. The previous floors (FEV1 and FVC at
 * 2 L, PEF at 350 L/min) sat inside the real patient distribution: on the 390-recording
 * labelled cohort, 20% of *measured* FEV1 values fell below 2 L. Those patients saw `--`
 * for FEV1 and FVC while FEV1/FVC still rendered, because the ratio band (0.4-1.0) admits
 * essentially every real ratio. The effect was worst for the obstructed patients the
 * product exists to monitor, and it also silently dropped them from their own trend charts
 * (see getSpirometryTrendValue), so their longitudinal view looked empty.
 *
 * Keep these wide. Narrowing them hides real patients; it does not improve accuracy.
 */
const VALIDATED_MEASUREMENT_RANGES = Object.freeze({
  fev1: Object.freeze({
    // Severe obstruction reaches well under 1 L; 8 L covers the largest healthy adults.
    lowerBound: 0.3,
    upperBound: 8,
    decimals: 2,
    inclusive: true,
  }),
  fvc: Object.freeze({
    lowerBound: 0.5,
    upperBound: 9,
    decimals: 2,
    inclusive: true,
  }),

  fev1FvcRatio: Object.freeze({
    // A ratio cannot exceed 1.0 by definition; severe obstruction can reach ~0.2.
    lowerBound: 0.15,
    upperBound: 1,
    decimals: 2,
    inclusive: true,
  }),
  pef: Object.freeze({
    lowerBound: 60,
    upperBound: 800,
    decimals: 0,
    inclusive: true,
  }),
  rr: Object.freeze({
    lowerBound: 5,
    upperBound: 40,
    decimals: 0,
    inclusive: true,
  }),
  hr: Object.freeze({
    lowerBound: 50,
    upperBound: 120,
    decimals: 0,
  }),
  hrv: Object.freeze({
    lowerBound: 0,
    upperBound: 150,
    decimals: 0,
  }),
});

function readFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function getValidatedMeasurementRange(metric) {
  return VALIDATED_MEASUREMENT_RANGES[metric] ?? null;
}

export function isMeasurementWithinValidatedRange(metric, value) {
  const numericValue = readFiniteNumber(value);
  if (numericValue == null) return false;

  const range = getValidatedMeasurementRange(metric);
  if (!range) return true;

  if (range.inclusive) {
    return numericValue >= range.lowerBound && numericValue <= range.upperBound;
  }

  return numericValue > range.lowerBound && numericValue < range.upperBound;
}

export function getValidatedMeasurementValue(metric, value) {
  const numericValue = readFiniteNumber(value);
  if (numericValue == null) return null;

  return isMeasurementWithinValidatedRange(metric, numericValue)
    ? numericValue
    : null;
}

export function formatValidatedMeasurement(metric, value, fallback = "--") {
  const numericValue = getValidatedMeasurementValue(metric, value);
  if (numericValue == null) return fallback;

  const range = getValidatedMeasurementRange(metric);
  const decimals = range?.decimals ?? 0;

  return decimals > 0
    ? numericValue.toFixed(decimals)
    : String(Math.round(numericValue));
}
